// chat.js
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

class AvatarChatService {
  constructor() {
    // Core objects
    this.speechRecognizer = null;
    this.avatarSynthesizer = null;
    this.peerConnection = null;
    this.messages = [];
    this.messageInitiated = false;
    this.dataSources = [];
    this.sentenceLevelPunctuations = ['.', '?', '!', ':', ';', '。', '？', '！', '：', '；'];
    this.enableDisplayTextAlignmentWithSpeech = true;
    this.enableQuickReply = false;
    this.quickReplies = ['Let me take a look.', 'Let me check.', 'One moment, please.'];
    this.byodDocRegex = new RegExp(/\[doc(\d+)\]/g);
    this.isSpeaking = false;
    this.speakingText = "";
    this.spokenTextQueue = [];
    this.sessionActive = false;
    this.lastSpeakTime = null;
    this.imgUrl = "";
    
    // NEW: Web Speech API flag and settings
    this.useWebSpeechAPI = false; // Set to true to use browser TTS, false for Azure TTS
    this.webSpeechVoice = null; // Will store selected voice
    this.webSpeechRate = 1.0; // Speech rate (0.1 to 10)
    this.webSpeechPitch = 1.0; // Speech pitch (0 to 2)
    this.webSpeechVolume = 1.0; // Speech volume (0 to 1)
    
    // Safety flags for disposal management
    this.isDisposed = false;
    this.isDisconnecting = false;
    this.isRecognizing = false;
    
    // Event callbacks
    this.onStatusChange = () => {};
    this.onConnectionStateChange = () => {};
    this.onSpeakingStateChange = () => {};
    this.onMessageReceived = () => {};
    this.onSubtitleUpdate = () => {};
    this.onVideoConnect = () => {};
    this.onAudioConnect = () => {};
    this.onError = () => {};
    
    // Initialize Web Speech API if available
    this.initializeWebSpeechAPI();
  }

  // =================== WEB SPEECH API INITIALIZATION ===================
  initializeWebSpeechAPI() {
    if ('speechSynthesis' in window) {
      console.log('Web Speech API is available');
      
      // Wait for voices to be loaded
      const loadVoices = () => {
        const voices = speechSynthesis.getVoices();
        if (voices.length > 0) {
          // Set default voice (prefer English voices)
          this.webSpeechVoice = voices.find(voice => voice.lang.startsWith('en')) || voices[0];
          console.log('Default Web Speech voice set:', this.webSpeechVoice.name);
        }
      };
      
      // Load voices immediately if available
      loadVoices();
      
      // Also listen for voice changes (some browsers load voices asynchronously)
      speechSynthesis.onvoiceschanged = loadVoices;
    } else {
      console.log('Web Speech API is not available in this browser');
    }
  }

  // =================== WEB SPEECH API CONFIGURATION ===================
  setWebSpeechConfig(options = {}) {
    const {
      useWebSpeech = false,
      voiceName = null,
      rate = 1.0,
      pitch = 1.0,
      volume = 1.0
    } = options;
    
    this.useWebSpeechAPI = useWebSpeech;
    this.webSpeechRate = Math.max(0.1, Math.min(10, rate));
    this.webSpeechPitch = Math.max(0, Math.min(2, pitch));
    this.webSpeechVolume = Math.max(0, Math.min(1, volume));
    
    if (voiceName) {
      const voices = speechSynthesis.getVoices();
      const selectedVoice = voices.find(voice => voice.name === voiceName);
      if (selectedVoice) {
        this.webSpeechVoice = selectedVoice;
        console.log('Web Speech voice changed to:', voiceName);
      } else {
        console.warn('Voice not found:', voiceName);
      }
    }
    
    console.log('Web Speech API configuration updated:', {
      enabled: this.useWebSpeechAPI,
      voice: this.webSpeechVoice?.name,
      rate: this.webSpeechRate,
      pitch: this.webSpeechPitch,
      volume: this.webSpeechVolume
    });
  }

  // =================== GET AVAILABLE VOICES ===================
  getAvailableVoices() {
    if ('speechSynthesis' in window) {
      return speechSynthesis.getVoices().map(voice => ({
        name: voice.name,
        lang: voice.lang,
        localService: voice.localService,
        default: voice.default
      }));
    }
    return [];
  }

  // =================== SAFETY CHECKS ===================
  isValidForOperation() {
    return !this.isDisposed && !this.isDisconnecting;
  }

  safeClose(resource, resourceName) {
    try {
      if (resource && typeof resource.close === 'function') {
        if (resource.isDisposed !== undefined && !resource.isDisposed) {
          console.log(`Closing ${resourceName}...`);
          resource.close();
        } else if (resource.isDisposed === undefined) {
          // Some resources don't have isDisposed property
          console.log(`Closing ${resourceName}...`);
          resource.close();
        }
      }
    } catch (error) {
      console.warn(`Error closing ${resourceName}:`, error.message);
      // Don't throw - continue with cleanup
    }
  }

  // =================== CONNECT AVATAR SERVICE ===================
  connectAvatar(config) {
    if (!this.isValidForOperation()) {
      this.onError('Service is disposed or disconnecting');
      return;
    }

    const {
      cogSvcRegion,
      cogSvcSubKey,
      privateEndpointEnabled,
      privateEndpoint,
      talkingAvatarCharacter,
      talkingAvatarStyle,
      backgroundImageUrl,
      sttLocales,
      ttsVoice,
      azureOpenAIEndpoint,
      azureOpenAIApiKey,
      azureOpenAIDeploymentName,
      systemPrompt,
      enableOyd,
      azureCogSearchEndpoint,
      azureCogSearchApiKey,
      azureCogSearchIndexName
    } = config;

    // Validate configuration
    if (!cogSvcSubKey) {
      this.onError('Please provide a valid Azure Speech API key');
      return;
    }

    let actualPrivateEndpoint = '';
    if (privateEndpointEnabled) {
      if (!privateEndpoint.startsWith('https://')) {
        this.onError('Please provide a valid Azure Speech endpoint (https://...)');
        return;
      }
      actualPrivateEndpoint = privateEndpoint.slice(8);
    }

    // Setup speech synthesis
    let speechSynthesisConfig;
    if (privateEndpointEnabled) {
      speechSynthesisConfig = SpeechSDK.SpeechConfig.fromEndpoint(
        new URL(`wss://${actualPrivateEndpoint}/tts/cognitiveservices/websocket/v1?enableTalkingAvatar=true`),
        cogSvcSubKey
      );
    } else {
      speechSynthesisConfig = SpeechSDK.SpeechConfig.fromSubscription(cogSvcSubKey, cogSvcRegion);
    }

    // Avatar config with background image support
    const avatarConfig = new SpeechSDK.AvatarConfig(talkingAvatarCharacter, talkingAvatarStyle);
    
    // Set background image if provided
    if (backgroundImageUrl && backgroundImageUrl.trim()) {
      try {
        new URL(backgroundImageUrl);
        avatarConfig.backgroundImage = backgroundImageUrl;
        console.log('Background image set:', backgroundImageUrl);
      } catch (error) {
        console.warn('Invalid background image URL provided:', backgroundImageUrl);
        this.onError('Invalid background image URL. Please provide a valid public URL.');
      }
    }
    
    this.avatarSynthesizer = new SpeechSDK.AvatarSynthesizer(speechSynthesisConfig, avatarConfig);
    this.avatarSynthesizer.avatarEventReceived = (s, e) => {
      let offsetMsg = (e.offset === 0) ? '' : `, offset from session start: ${e.offset / 10000}ms.`;
      console.log('Avatar event: ' + e.description + offsetMsg);
    };

    // Prepare STT config
    const speechRecognitionConfig = SpeechSDK.SpeechConfig.fromEndpoint(
      new URL(`wss://${cogSvcRegion}.stt.speech.microsoft.com/speech/universal/v2`),
      cogSvcSubKey
    );
    speechRecognitionConfig.setProperty(
      SpeechSDK.PropertyId.SpeechServiceConnection_LanguageIdMode,
      "Continuous"
    );
    
    const sttLocalesList = sttLocales.split(',');
    const autoDetectSourceLanguageConfig = SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(sttLocalesList);
    
    this.speechRecognizer = SpeechSDK.SpeechRecognizer.FromConfig(
      speechRecognitionConfig,
      autoDetectSourceLanguageConfig,
      SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()
    );

    // Check Azure OpenAI
    if (!azureOpenAIEndpoint || !azureOpenAIApiKey || !azureOpenAIDeploymentName) {
      this.onError('Please provide valid Azure OpenAI credentials');
      return;
    }

    // Initialize data sources for "On Your Data" if enabled
    this.dataSources = [];
    if (enableOyd) {
      if (!azureCogSearchEndpoint || !azureCogSearchApiKey || !azureCogSearchIndexName) {
        this.onError('Please provide valid Azure Cognitive Search credentials');
        return;
      }
      this.setDataSources(azureCogSearchEndpoint, azureCogSearchApiKey, azureCogSearchIndexName, systemPrompt);
    }

    // Initialize messages with system prompt
    if (!this.messageInitiated) {
      this.messages = [];
      if (this.dataSources.length === 0) {
        this.messages.push({ role: 'system', content: systemPrompt });
      }
      this.messageInitiated = true;
    }

    this.onStatusChange('Connecting to avatar service...');
    this.onConnectionStateChange('connecting');

    // Get token from TTS service
    const xhr = new XMLHttpRequest();
    if (privateEndpointEnabled) {
      xhr.open("GET", `https://${actualPrivateEndpoint}/tts/cognitiveservices/avatar/relay/token/v1`);
    } else {
      xhr.open("GET", `https://${cogSvcRegion}.tts.speech.microsoft.com/cognitiveservices/avatar/relay/token/v1`);
    }
    xhr.setRequestHeader("Ocp-Apim-Subscription-Key", cogSvcSubKey);
    xhr.addEventListener("readystatechange", () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          try {
            const responseData = JSON.parse(xhr.responseText);
            const iceServerUrl = responseData.Urls[0];
            const iceServerUsername = responseData.Username;
            const iceServerCredential = responseData.Password;
            this.setupWebRTC(iceServerUrl, iceServerUsername, iceServerCredential);
          } catch (error) {
            this.onError('Failed to parse token response: ' + error.message);
            this.onConnectionStateChange('error');
          }
        } else {
          this.onError('Failed to get token: ' + xhr.status);
          this.onConnectionStateChange('error');
        }
      }
    });
    xhr.send();
  }

  // =================== UPDATE BACKGROUND IMAGE ===================
  updateBackgroundImage(backgroundImageUrl) {
    if (!this.isValidForOperation()) {
      this.onError('Service is disposed or disconnecting');
      return false;
    }

    if (!this.avatarSynthesizer) {
      this.onError('Avatar not initialized. Please connect avatar first.');
      return false;
    }

    if (!backgroundImageUrl || !backgroundImageUrl.trim()) {
      this.onError('Please provide a valid background image URL.');
      return false;
    }

    try {
      new URL(backgroundImageUrl);
      this.onStatusChange('Background image update requires avatar reconnection.');
      console.log('To update background image, reconnect avatar with new backgroundImageUrl:', backgroundImageUrl);
      return true;
    } catch (error) {
      this.onError('Invalid background image URL format.');
      return false;
    }
  }

  // =================== SETUP WEBSOCKET & AVATAR ===================
  setupWebRTC(iceServerUrl, iceServerUsername, iceServerCredential) {
    if (!this.isValidForOperation()) {
      this.onError('Service is disposed or disconnecting');
      return;
    }

    this.peerConnection = new RTCPeerConnection({
      iceServers: [{
        urls: [iceServerUrl],
        username: iceServerUsername,
        credential: iceServerCredential
      }]
    });

    this.peerConnection.ontrack = (event) => {
      if (!this.isValidForOperation()) return;
      
      if (event.track.kind === 'audio') {
        const audioStream = new MediaStream([event.track]);
        this.onAudioConnect(audioStream);
        console.log('WebRTC audio connected.');
      } else if (event.track.kind === 'video') {
        const videoStream = new MediaStream([event.track]);
        this.onVideoConnect(videoStream);
        console.log('WebRTC video connected.');
        
        setTimeout(() => { 
          if (this.isValidForOperation()) {
            this.sessionActive = true;
            this.onConnectionStateChange('connected');
            this.onStatusChange('Avatar session ready!');
          }
        }, 5000);
      }
    };

    this.peerConnection.addEventListener("datachannel", event => {
      if (!this.isValidForOperation()) return;
      
      const dataChannel = event.channel;
      dataChannel.onmessage = e => {
        if (!this.isValidForOperation()) return;
        
        const webRTCEvent = JSON.parse(e.data);
        console.log('[WebRTC event] ' + e.data);
        this.onSubtitleUpdate(webRTCEvent.text || '');
      };
    });
    this.peerConnection.createDataChannel("eventChannel");

    this.peerConnection.oniceconnectionstatechange = () => {
      console.log("ICE state: " + this.peerConnection.iceConnectionState);
    };

    this.peerConnection.addTransceiver('video', { direction: 'sendrecv' });
    this.peerConnection.addTransceiver('audio', { direction: 'sendrecv' });

    this.avatarSynthesizer.startAvatarAsync(this.peerConnection)
    .then(r => {
      if (!this.isValidForOperation()) return;
      
      if (r.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
        console.log('Avatar started, resultId:' + r.resultId);
      } else {
        console.log('Unable to start avatar, resultId:' + r.resultId);
        if (r.reason === SpeechSDK.ResultReason.Canceled) {
          const cancellationDetails = SpeechSDK.CancellationDetails.fromResult(r);
          console.log('Avatar canceled: ' + cancellationDetails.errorDetails);
          this.onError('Avatar startup canceled: ' + cancellationDetails.errorDetails);
        }
        this.onConnectionStateChange('error');
      }
    })
    .catch(error => {
      if (!this.isValidForOperation()) return;
      
      console.log('Avatar failed to start: ' + error);
      this.onError('Avatar failed to start: ' + error.message);
      this.onConnectionStateChange('error');
    });
  }

  // =================== DISCONNECT AVATAR ===================
  async disconnectAvatar() {
    // Prevent multiple disconnection attempts
    if (this.isDisconnecting || this.isDisposed) {
      console.log('Already disconnecting or disposed');
      return;
    }
    
    this.isDisconnecting = true;
    this.sessionActive = false;
    
    try {
      console.log('Starting avatar disconnection...');
      
      // Stop speaking first
      this.spokenTextQueue = [];
      this.isSpeaking = false;
      
      // Stop Web Speech API if active
      if (this.useWebSpeechAPI && 'speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
      
      // Stop microphone if it's running
      if (this.isRecognizing) {
        await this.stopMicrophone().catch(err => 
          console.warn('Error stopping microphone during disconnect:', err)
        );
      }
      
      // Close speech recognizer
      if (this.speechRecognizer) {
        this.safeClose(this.speechRecognizer, 'speech recognizer');
        this.speechRecognizer = null;
      }
      
      // Stop avatar synthesizer
      if (this.avatarSynthesizer) {
        try {
          await this.avatarSynthesizer.stopSpeakingAsync().catch(err => 
            console.warn('Error stopping avatar speaking:', err)
          );
        } catch (error) {
          console.warn('Error during avatar stop speaking:', error);
        }
        
        this.safeClose(this.avatarSynthesizer, 'avatar synthesizer');
        this.avatarSynthesizer = null;
      }
      
      // Close peer connection
      if (this.peerConnection) {
        try {
          this.peerConnection.close();
          this.peerConnection = null;
        } catch (error) {
          console.warn('Error closing peer connection:', error);
        }
      }
      
      this.isDisposed = true;
      this.onConnectionStateChange('disconnected');
      this.onStatusChange('Avatar disconnected');
      
      console.log('Avatar disconnection completed');
      
    } catch (error) {
      console.error('Error during disconnect:', error);
      this.onError('Error during disconnect: ' + error.message);
    } finally {
      this.isDisconnecting = false;
    }
  }

  // =================== SET DATA SOURCES ===================
  setDataSources(azureCogSearchEndpoint, azureCogSearchApiKey, azureCogSearchIndexName, roleInformation) {
    this.dataSources.push({
      type: 'AzureCognitiveSearch',
      parameters: {
        endpoint: azureCogSearchEndpoint,
        key: azureCogSearchApiKey,
        indexName: azureCogSearchIndexName,
        semanticConfiguration: '',
        queryType: 'simple',
        fieldsMapping: {
          contentFieldsSeparator: '\n',
          contentFields: ['content'],
          filepathField: null,
          titleField: 'title',
          urlField: null
        },
        inScope: true,
        roleInformation: roleInformation
      }
    });
  }

  // =================== HTML ENCODE HELPER ===================
  htmlEncode(text) {
    const entityMap = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;'
    };
    return String(text).replace(/[&<>"'\/]/g, (m) => entityMap[m]);
  }

  // =================== WEB SPEECH TTS FUNCTION ===================
  speakWithWebSpeech(text) {
    if (!('speechSynthesis' in window)) {
      console.error('Web Speech API not available');
      return Promise.reject('Web Speech API not available');
    }

    return new Promise((resolve, reject) => {
      // Cancel any ongoing speech
      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Set voice and speech parameters
      if (this.webSpeechVoice) {
        utterance.voice = this.webSpeechVoice;
      }
      utterance.rate = this.webSpeechRate;
      utterance.pitch = this.webSpeechPitch;
      utterance.volume = this.webSpeechVolume;

      // Set up event listeners
      utterance.onstart = () => {
        console.log('Web Speech started:', text);
      };

      utterance.onend = () => {
        console.log('Web Speech ended:', text);
        resolve();
      };

      utterance.onerror = (event) => {
        console.error('Web Speech error:', event.error);
        reject(event.error);
      };

      // Speak the text
      speechSynthesis.speak(utterance);
    });
  }

  // =================== ENHANCED TTS FUNCTION ===================
  speak(text, ttsVoice, endingSilenceMs = 0) {
    if (!this.isValidForOperation()) {
      console.warn('Cannot speak: service is disposed or disconnecting');
      return;
    }

    if (this.isSpeaking) {
      this.spokenTextQueue.push({ text, ttsVoice, endingSilenceMs });
      return;
    }
    this.speakNext(text, ttsVoice, endingSilenceMs);
  }

  speakNext(text, ttsVoice, endingSilenceMs = 0) {
    if (!this.isValidForOperation()) {
      console.warn('Cannot speak: service is disposed');
      return;
    }

    this.lastSpeakTime = new Date();
    this.isSpeaking = true;
    this.speakingText = text;
    this.onSpeakingStateChange(true);

    // Choose TTS method based on flag
    if (this.useWebSpeechAPI) {
      console.log('Using Web Speech API for TTS');
      this.speakWithWebSpeech(text)
        .then(() => {
          if (!this.isValidForOperation()) return;
          
          this.lastSpeakTime = new Date();
          this.processNextSpeech(ttsVoice);
        })
        .catch(error => {
          if (!this.isValidForOperation()) return;
          
          console.log('Web Speech error:', error);
          this.processNextSpeech(ttsVoice);
        });
    } else {
      console.log('Using Azure TTS');
      this.speakWithAzureTTS(text, ttsVoice, endingSilenceMs);
    }
  }

  speakWithAzureTTS(text, ttsVoice, endingSilenceMs = 0) {
    if (!this.avatarSynthesizer) {
      console.warn('Cannot speak: avatar synthesizer not available');
      this.processNextSpeech(ttsVoice);
      return;
    }

    let ssml = `<speak version='1.0' xmlns='http://www.w3.org/2001/10/synthesis'
                   xmlns:mstts='http://www.w3.org/2001/mstts'
                   xml:lang='en-US'>
                <voice name='${ttsVoice}'>
                  <mstts:ttsembedding>
                    <mstts:leadingsilence-exact value='0'/>
                    ${this.htmlEncode(text)}
                  </mstts:ttsembedding>
                </voice>
              </speak>`;

    if (endingSilenceMs > 0) {
      ssml = `<speak version='1.0' xmlns='http://www.w3.org/2001/10/synthesis'
                   xmlns:mstts='http://www.w3.org/2001/mstts'
                   xml:lang='en-US'>
                <voice name='${ttsVoice}'>
                  <mstts:ttsembedding>
                    <mstts:leadingsilence-exact value='0'/>
                    ${this.htmlEncode(text)}
                    <break time='${endingSilenceMs}ms' />
                  </mstts:ttsembedding>
                </voice>
              </speak>`;
    }

    this.avatarSynthesizer.speakSsmlAsync(ssml)
      .then(result => {
        if (!this.isValidForOperation()) return;
        
        if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
          console.log(`Azure TTS completed: ${text}, resultId: ${result.resultId}`);
          this.lastSpeakTime = new Date();
        } else {
          console.log(`Azure TTS error. resultId: ${result.resultId}`);
        }
        
        this.processNextSpeech(ttsVoice);
      })
      .catch(error => {
        if (!this.isValidForOperation()) return;
        
        console.log(`Azure TTS error: ${error}`);
        this.processNextSpeech(ttsVoice);
      });
  }

  processNextSpeech(ttsVoice) {
    if (this.spokenTextQueue.length > 0 && this.isValidForOperation()) {
      const nextItem = this.spokenTextQueue.shift();
      this.speakNext(nextItem.text, nextItem.ttsVoice || ttsVoice, nextItem.endingSilenceMs);
    } else {
      this.isSpeaking = false;
      this.onSpeakingStateChange(false);
    }
  }

  stopSpeaking() {
    if (!this.isValidForOperation()) {
      return;
    }

    this.spokenTextQueue = [];
    
    // Stop Web Speech API if active
    if (this.useWebSpeechAPI && 'speechSynthesis' in window) {
      speechSynthesis.cancel();
      this.isSpeaking = false;
      this.onSpeakingStateChange(false);
      console.log('Web Speech stopped.');
      return;
    }
    
    // Stop Azure TTS if active
    if (this.avatarSynthesizer) {
      this.avatarSynthesizer.stopSpeakingAsync()
        .then(() => {
          this.isSpeaking = false;
          this.onSpeakingStateChange(false);
          console.log('Azure TTS stop request sent.');
        })
        .catch(err => {
          console.log(`Error stopping Azure TTS: ${err}`);
          if (this.isValidForOperation()) {
            this.onError('Failed to stop speaking: ' + err.message);
          }
        });
    }
  }

  // =================== HANDLE USER QUERY ===================
  handleUserQuery(userQuery, userQueryHTML, imgUrlPath, config) {
    if (!this.isValidForOperation()) {
      this.onError('Service is disposed or disconnecting');
      return;
    }

    let contentMessage = userQuery;
    if (imgUrlPath && imgUrlPath.trim()) {
      contentMessage = [
        { type: "text", text: userQuery },
        { type: "image_url", image_url: { url: imgUrlPath } }
      ];
    }
    
    this.messages.push({ role: 'user', content: contentMessage });
    this.onMessageReceived({
      role: 'user',
      content: userQuery,
      hasImage: !!imgUrlPath
    });

    if (this.isSpeaking) this.stopSpeaking();
    
    if (this.dataSources.length > 0 && this.enableQuickReply) {
      const quickReply = this.getQuickReply();
      this.speak(quickReply, config.ttsVoice, 2000);
    }

    const { azureOpenAIEndpoint, azureOpenAIApiKey, azureOpenAIDeploymentName } = config;

    let url = `${azureOpenAIEndpoint}/openai/deployments/${azureOpenAIDeploymentName}/chat/completions?api-version=2023-06-01-preview`;
    let body = JSON.stringify({ messages: this.messages, stream: true });

    if (this.dataSources.length > 0) {
      url = `${azureOpenAIEndpoint}/openai/deployments/${azureOpenAIDeploymentName}/extensions/chat/completions?api-version=2023-06-01-preview`;
      body = JSON.stringify({ dataSources: this.dataSources, messages: this.messages, stream: true });
    }

    let assistantReply = '';
    let toolContent = '';
    let spokenSentence = '';

    this.onMessageReceived({
      role: 'assistant',
      content: '',
      isComplete: false
    });

    fetch(url, {
      method: 'POST',
      headers: {
        'api-key': azureOpenAIApiKey,
        'Content-Type': 'application/json'
      },
      body
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Chat API response: ${response.status} ${response.statusText}`);
      }
      
      const reader = response.body.getReader();

      const read = (previousChunkString = '') => {
        return reader.read().then(({ value, done }) => {
          if (done || !this.isValidForOperation()) return;
          
          let chunkString = new TextDecoder().decode(value, { stream: true });
          if (previousChunkString) {
            chunkString = previousChunkString + chunkString;
          }

          if (!chunkString.endsWith('}\n\n') && !chunkString.endsWith('[DONE]\n\n')) {
            return read(chunkString);
          }
          
          chunkString.split('\n\n').forEach(line => {
            if (!this.isValidForOperation()) return;
            
            if (line.startsWith('data:') && !line.endsWith('[DONE]')) {
              try {
                const responseJson = JSON.parse(line.substring(5).trim());
                let responseToken;
                
                if (this.dataSources.length === 0) {
                  responseToken = responseJson.choices[0].delta.content;
                } else {
                  const role = responseJson.choices[0].messages[0].delta.role;
                  if (role === 'tool') {
                    toolContent = responseJson.choices[0].messages[0].delta.content;
                  } else {
                    responseToken = responseJson.choices[0].messages[0].delta.content || '';
                    if (this.byodDocRegex.test(responseToken)) {
                      responseToken = responseToken.replace(this.byodDocRegex, '').trim();
                    }
                    if (responseToken === '[DONE]') responseToken = undefined;
                  }
                }
                
                if (responseToken && this.isValidForOperation()) {
                  assistantReply += responseToken;
                  
                  this.onMessageReceived({
                    role: 'assistant',
                    content: assistantReply,
                    isComplete: false,
                    token: responseToken
                  });
                  
                  if (responseToken === '\n' || responseToken === '\n\n') {
                    spokenSentence += responseToken;
                    this.speak(spokenSentence, config.ttsVoice);
                    spokenSentence = '';
                  } else {
                    spokenSentence += responseToken;
                    const trimmed = responseToken.replace(/\n/g, '');
                    if (trimmed.length <= 2) {
                      for (let punct of this.sentenceLevelPunctuations) {
                        if (trimmed.startsWith(punct)) {
                          this.speak(spokenSentence, config.ttsVoice);
                          spokenSentence = '';
                          break;
                        }
                      }
                    }
                  }
                }
              } catch (err) {
                console.log('Error parsing chunk: ' + err);
              }
            }
          });
          
          return read();
        });
      };
      
      return read();
    })
    .then(() => {
      if (!this.isValidForOperation()) return;
      
      if (spokenSentence) {
        this.speak(spokenSentence, config.ttsVoice);
        spokenSentence = '';
      }
      
      if (this.dataSources.length > 0 && toolContent) {
        this.messages.push({ role: 'tool', content: toolContent });
      }
      
      this.messages.push({ role: 'assistant', content: assistantReply });
      
      this.onMessageReceived({
        role: 'assistant',
        content: assistantReply,
        isComplete: true
      });
    })
    .catch(err => {
      if (!this.isValidForOperation()) return;
      
      console.log('fetch error: ' + err);
      this.onError('Failed to get response: ' + err.message);
    });
  }

  getQuickReply() {
    return this.quickReplies[Math.floor(Math.random() * this.quickReplies.length)];
  }

  // =================== MICROPHONE CONTROL ===================
  startMicrophone(config) {
    return new Promise((resolve, reject) => {
      if (!this.isValidForOperation()) {
        reject('Service is disposed or disconnecting');
        return;
      }

      if (!this.speechRecognizer) {
        reject('Speech recognizer not initialized');
        return;
      }
      
      if (this.isRecognizing) {
        resolve('Microphone already running');
        return;
      }
      
      this.speechRecognizer.recognized = (s, e) => {
        if (!this.isValidForOperation()) return;
        
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const userQuery = e.result.text.trim();
          if (!userQuery) return;
          
          this.stopMicrophone()
            .then(() => {
              if (this.isValidForOperation()) {
                this.handleUserQuery(userQuery, "", "", config);
                resolve(userQuery);
              }
            })
            .catch(err => {
              if (this.isValidForOperation()) {
                reject(err);
              }
            });
        }
      };
      
      this.speechRecognizer.startContinuousRecognitionAsync(
        () => {
          if (this.isValidForOperation()) {
            this.isRecognizing = true;
            this.onStatusChange('Microphone started, listening...');
            resolve('Microphone started');
          }
        },
        err => {
          console.log("Failed to start recognition:", err);
          if (this.isValidForOperation()) {
            this.onError('Failed to start microphone: ' + err);
            reject(err);
          }
        }
      );
    });
  }

  stopMicrophone() {
    return new Promise((resolve, reject) => {
      if (!this.isRecognizing) {
        resolve('Microphone not running');
        return;
      }

      if (!this.speechRecognizer) {
        this.isRecognizing = false;
        resolve('Speech recognizer already disposed');
        return;
      }
      
      // Check if already disposed
      if (this.speechRecognizer.isDisposed) {
        this.isRecognizing = false;
        resolve('Speech recognizer already disposed');
        return;
      }
      
      this.speechRecognizer.stopContinuousRecognitionAsync(
        () => {
          this.isRecognizing = false;
          if (this.isValidForOperation()) {
            this.onStatusChange('Microphone stopped');
          }
          resolve('Microphone stopped');
        },
        err => {
          this.isRecognizing = false;
          console.log("Error stopping recognition:", err);
          if (this.isValidForOperation()) {
            this.onError('Failed to stop microphone: ' + err);
            reject(err);
          } else {
            // If we're disposing, don't treat this as an error
            resolve('Microphone stopped during disposal');
          }
        }
      );
    });
  }

  // =================== CHAT HISTORY MANAGEMENT ===================
  clearChatHistory(systemPrompt) {
    this.messages = [];
    
    // Re-add system message if not using data sources
    if (this.dataSources.length === 0 && systemPrompt) {
      this.messages.push({ role: 'system', content: systemPrompt });
    }
    
    this.onStatusChange('Chat history cleared');
    return this.messages;
  }

  // =================== HEALTH CHECK ===================
  checkHealth() {
    if (!this.lastSpeakTime) return true;
    
    const now = new Date();
    if (now - this.lastSpeakTime > 25000) {
      this.disconnectAvatar();
      return false;
    }
    return true;
  }
}

export default AvatarChatService;
